import classNames from "classnames";
import * as React from "react";

import { Table, type TableProps } from "@/shared/foundation/primitives";

import styles from "./KeyValueTable.module.css";

export interface KeyValueTableProps extends Omit<TableProps, "layout"> {
  /** The heading over the keys. */
  keyHeading: string;
  /** The heading over the values. */
  valueHeading: string;
  /** Id put on the value heading, so a control in that column can be labelled by it. */
  valueHeadingId?: string;
  children: React.ReactNode;
}

/**
 * Two columns, a key and what it holds, one pair to a row: the table the
 * agent form lists its variables in, and the detail page reads them back
 * from.
 */
function KeyValueTableRoot({
  keyHeading,
  valueHeading,
  valueHeadingId,
  className,
  children,
  ...rest
}: KeyValueTableProps) {
  return (
    <Table
      layout="fixed"
      className={classNames(styles.table, className)}
      {...rest}
    >
      <colgroup>
        <col className={styles.keyColumn} />
        <col className={styles.valueColumn} />
      </colgroup>
      <Table.Header>
        <Table.Row>
          <Table.Head scope="col">{keyHeading}</Table.Head>
          <Table.Head scope="col" id={valueHeadingId}>
            {valueHeading}
          </Table.Head>
        </Table.Row>
      </Table.Header>
      <Table.Body>{children}</Table.Body>
    </Table>
  );
}

export interface KeyValueRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  /** The key, set as the row's heading. */
  label: React.ReactNode;
  /** Extra styling for the key cell (a mono face for a variable's name). */
  labelClassName?: string;
  /** The value cell. */
  children: React.ReactNode;
}

function Row({ label, labelClassName, children, ...rest }: KeyValueRowProps) {
  return (
    <Table.Row {...rest}>
      <Table.Head
        scope="row"
        className={classNames(styles.key, labelClassName)}
      >
        {label}
      </Table.Head>
      <Table.Cell>{children}</Table.Cell>
    </Table.Row>
  );
}

/** The one row a table with nothing to list carries, spanning both columns. */
function EmptyRow({
  children,
  ...rest
}: React.HTMLAttributes<HTMLTableRowElement>) {
  return (
    <Table.Row {...rest}>
      <Table.Cell colSpan={2} className={styles.emptyCell}>
        {children}
      </Table.Cell>
    </Table.Row>
  );
}

export const KeyValueTable = Object.assign(KeyValueTableRoot, {
  Row,
  EmptyRow,
});
